<template>
  <div id="organisation-creator">
    <b-button-close
      class="close"
      data-test="organisationCreation-cancel"
      @click="$emit('cancel')"
    />
    <h6>{{ $t('organisationCreation.title') }}</h6>
    <br>
    <form>

      <!-- ADDITIONNAL_ID -->
      <div
        v-if="isFieldVisible('additional_id', formConfig.hiddenFields)"
        class="form-row"
      >
        <div class="form-group col-12">
          <ValidationProvider
            :rules="isFieldRequired('additional_id', formConfig.requiredFields)"
            v-slot="{ classes, errors }"
          >
            <div class="control" :class="classes">
              <label
                :class="isFieldRequired('additional_id', formConfig.requiredFields)"
                for="organisationCreation-additional_id"
              >
                {{ $t('organisationCreation.form.id') }}
              </label>
              <p>{{ $t('organisationCreation.form.idHelp') }}</p>
              <input
                v-model="formData.additional_id"
                id="organisationCreation-additional_id"
                data-test="organisationCreation-additional_id"
                class="form-control"
                type="text"
                placeholder=""
              >
              <span class="form-errors">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
      </div>

      <!-- NAME -->
      <div
        v-if="isFieldVisible('display_name', formConfig.hiddenFields)"
        class="form-row"
      >
        <div class="form-group col-12">
          <ValidationProvider
            :rules="isFieldRequired('display_name', formConfig.requiredFields)"
            v-slot="{ classes, errors }"
          >
            <div class="control" :class="classes">
              <label
                :class="isFieldRequired('display_name', formConfig.requiredFields)"
                for="organisationCreation-display_name"
              >
                {{ $t('organisationCreation.form.name') }}
              </label>
              <p>{{ $t('organisationCreation.form.nameHelp') }}</p>
              <input
                v-model="formData.display_name"
                id="organisationCreation-display_name"
                data-test="organisationCreation-display_name"
                class="form-control"
                type="text"
                placeholder=""
              >
              <span class="form-errors">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
      </div>

      <!-- ACRONYM -->
      <div
        v-if="isFieldVisible('acronym', formConfig.hiddenFields)"
        class="form-row"
      >
        <div class="form-group col-6">
          <div class="control">
            <label for="organisationCreation-acronym">
              {{ $t('organisationCreation.form.acronym') }}
            </label>
            <input
              v-model="formData.acronym"
              id="organisationCreation-acronym"
              data-test="organisationCreation-acronym"
              class="form-control"
              type="text"
              placeholder=""
            >
          </div>
        </div>
      </div>

      <!-- SIRET -->
      <div
        v-if="isFieldVisible('registration_number', formConfig.hiddenFields)"
        class="form-row"
      >
        <div class="form-group col-6">
          <label for="organisationCreation-registration_number">
            {{ $t('organisationCreation.form.siret') }}
          </label>
          <input
            v-model="formData.registration_number"
            id="organisationCreation-registration_number"
            data-test="organisationCreation-registration_number"
            class="form-control"
            type="text"
            placeholder="xxx xxx xxx xxxxx"
          >
        </div>
      </div>

      <!-- TYPE -->
      <div
        v-if="isFieldVisible('organisation_type', formConfig.hiddenFields)"
        class="form-row"
      >
        <div class="form-group col-6">
          <ValidationProvider
            :rules="isFieldRequired('organisation_type', formConfig.requiredFields)"
            v-slot="{ classes, errors }"
          >
            <div class="control" :class="classes">
              <label
                :class="isFieldRequired('organisation_type', formConfig.requiredFields)"
                for="organisationCreation-organisation_type"
              >
                {{ $t('organisationCreation.form.type') }}
              </label>
              <Multiselect
                v-model="formData.organisation_type"
                :options="organisationsTypes"
                :class="classes"
                id="organisationCreation-organisation_type"
                data-test="organisationCreation-organisation_type"
                track-by="codename"
                label="display_name"
                selectLabel=""
                selectedLabel=""
                deselectLabel=""
                :searchable="false"
                :placeholder="$t('organisationCreation.form.typePlaceholder')"
              />
              <span class="form-errors">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
      </div>

      <!-- LOGO -->
      <div 
        v-if="isFieldVisible('logo', formConfig.hiddenFields) && !showSimpleCreationForm"
        class="form-row"
      >
        <div class="form-group col-6">
          <label for="organisationCreation-logo">
            {{ $t('organisationCreation.form.logo') }}
          </label>
          <p>{{ $t('organisationCreation.form.logoHelp') }}</p>
          <ValidationProvider ref="thumbnail" v-slot="{ classes, errors }">
            <div class="control" :class="classes">
              <ImportImage
                id="organisationCreation-logo"
                name="logo"
                :title="null"
                :file="null"
                @update="setThumbnail"
              />
              <span class="form-errors">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
      </div>

      <!-- WEBSITE -->
      <div class="form-row">
        <div class="form-group col-9">
          <label for="organisationCreation-website_url">
            {{ $t('organisationCreation.form.web') }}
          </label>
          <input
            v-model="formData.website_url"
            id="organisationCreation-website_url"
            data-test="organisationCreation-website_url"
            class="form-control"
            type="text"
            placeholder="https://"
            @focus="formData.website_url === null ? formData.website_url = 'https://' : null"
            @blur="formData.website_url === 'https://' ? formData.website_url = null : null"
          >
        </div>
      </div>

      <!-- PHONE -->
      <div class="form-row">
        <div class="form-group col-6">
          <label for="organisationCreation-phone_number">
            {{ $t('organisationCreation.form.phone') }}
          </label>
          <ValidationProvider
            ref="phone_number"
            :rules="isFieldRequired('phone_number', formConfig.requiredFields)"
            v-slot="{ classes, errors }"
          >
            <div class="control" :class="classes">
              <input
                v-model="formData.phone_number"
                id="organisationCreation-phone_number"
                data-test="organisationCreation-phone_number"
                class="form-control"
                type="text"
                placeholder=""
              >
              <span class="form-errors">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
      </div>

      <!-- ADDRESS -->
      <div class="form-row">
        <div class="form-group col-12">
          <label for="organisationCreation-postal_address">
            {{ $t('organisationCreation.form.address') }}
          </label>
          <textarea
            v-model="formData.postal_address"
            id="organisationCreation-postal_address"
            data-test="organisationCreation-postal_address"
            class="form-control"
          />
        </div>
      </div>

      <!-- DESCRIPTION -->
      <div class="form-row">
        <div class="form-group col-12">
          <label for="organisationCreation-description">
            {{ $t('organisationCreation.form.description') }}
          </label>
          <textarea
            v-model="formData.description"
            id="organisationCreation-description"
            data-test="organisationCreation-description"
            class="form-control"
          />
        </div>
      </div>

      <div class="form-row" v-if="!showSimpleCreationForm">
        <div class="form-group col-12">
          <label for="organisationCreation-groupOfOrganisation">
            {{ $t('organisationCreation.form.group.title') }}
          </label>
          <p>
            {{ $t('organisationCreation.form.group.help') }}
          </p>
          <SearchUsergroups
            id="organisationCreation-groupOfOrganisation"
            :type="'group-of-organisation'"
            :placeholder="$t('organisationCreation.form.group.placeholder')"
            @select="addOrgToSphere"
          />
          <div
            v-if="spheres.length > 0"
            id="orga-spheres-container"
          >
            <div
              v-for="sphere of spheres"
              :key="sphere.id"
              class="orga-sphere"
            >
              {{ sphere.display_name }}
              <b-icon-x
                font-scale="1.5"
                data-test="organisationCreation-removeOrgFromSphere" 
                @click="removeOrgFromSphere"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>

import i18n from '@/i18n';

import ImportImage from '@/components/ImportImage';
import SearchUsergroups from '@/components/SearchUsergroups';
import organisationAPI from '@/api/organisationsAPI.js';

import {
  ValidationProvider,
  extend,
  configure
} from 'vee-validate';
import {
  required
} from 'vee-validate/dist/rules';


extend('required', {
  ...required,
  message: () => i18n.t('errors.required')
});

configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid'
  }
});

export default {
  name: 'OrganisationCreation',

  components: {
    ValidationProvider,
    ImportImage,
    SearchUsergroups
  },

  props: {
    name: {
      type: String,
      default: null
    },
    apiError: {
      type: [Object, Array, String, null],
      default: null
    },
    showSimpleCreationForm: {
      type: Boolean,
      default: false
    },
    siret: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      organisationsTypes: [],
      formData: {
        additional_id: null,
        display_name: this.name || null,
        acronym: null,
        registration_number: this.siret || null,
        organisation_type: null,
        website_url: null,
        postal_address: null,
        phone_number: null,
        description: null,
        isValid: false,
      },
      thumbnail: null,
      spheres: []
    };
  },

  computed: {

    formConfig() {
      return this.$config.forms.organisation;
    },
  },

  watch: {
    formData: {
      deep: true,
      handler(newValue) {
        this.$emit('update', {
          form: newValue,
          thumbnail: this.thumbnail,
          spheres: this.spheres
        });
      }
    },
    thumbnail(newValue) {
      this.$emit('update', {
        form: this.formData,
        thumbnail: newValue,
        spheres: this.spheres
      });
    },
    spheres: {
      deep: true,
      handler(newValue) {
        this.$emit('update', {
          form: this.formData,
          thumbnail: this.thumbnail,
          spheres: newValue
        });
      }
    },
    apiError(newValue) {
      if (newValue && newValue.usergroup_roles && newValue.usergroup_roles.length && newValue.usergroup_roles[0].organisation) {
        for (const [key, value] of Object.entries(newValue.usergroup_roles[0].organisation)) {
          if (this.$refs[key]) {
            this.$refs[key].applyResult({
              errors: value,
              valid: false,
              failedRules: {},
            });
          }
        }
      }
    }
  },

  created() {
    if (this.organisationsTypes.length === 0) {
        organisationAPI.getOrganisationsTypes().then((response) => {
        this.organisationsTypes = response;
      });
    }
    this.$emit('update', {
      form: this.formData,
      thumbnail: this.thumbnail,
      spheres: this.spheres
    });
  },

  methods: {
    

    setThumbnail(e) {
      const formData = new FormData();
      formData.append('file', e);
      this.thumbnail = formData;
    },

    addOrgToSphere(e) {
      this.spheres.push(e);
    },
    removeOrgFromSphere(e) {
      const index = this.spheres.findIndex(el => el.id === e.id);
      this.spheres.splice(index, 1);
    },
  }
}
</script>

<style lang="less" scoped>
#organisation-creator {
  width: 100%;
}

h2 {
  color: @blue;
  margin-top: 0.5em;
  margin-left: 0.5em;
}

h4 {
  color: @blue;
  margin-top: 0.8em;
}

button {
  margin: 1em 2em 0 0;
  font-size: 1em;
}

.close {
  font-size: 2rem;
  position: relative;
  display: block;
  float: right;
  top: -2.5rem;
  margin-right: 1rem;
}

#orga-spheres-container {
  display: flex;
  flex-flow: row wrap;
  padding: 0.1em;
  margin: 0.5em 0;
  .orga-sphere {
    display: flex;
    margin: 0.2em 1em 0.2em 0;
    padding: 0.5em;
    border-radius: 10px;
    font-size: 0.9em;
    background-color: @blue;
    color: white;
    .b-icon {
      cursor: pointer;
    }
  }
}

</style>
